import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PlatformCommand } from '@shared/commands/command-base';
import { IPlatformToolHandler } from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import { InvalidateSubject } from '@shared/helpers/invalidate.subject';
import { MagicLinkModel, TabModel } from '@shared/models/platform-tool-models';
import { GridValueChangeEvent } from '@shared/models/common.models';
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import { CommandService } from '@core/services/command.service';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { DialogService } from '@progress/kendo-angular-dialog';
import { SnackBarService } from '@core/services/snack-bar.service';
import { PlatformSidebarState } from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import { Store } from '@ngrx/store';
import { IQueryState } from '@shared/store/reducers/query.reducer';
import { take } from 'rxjs';
import { FileSelectorModel } from '@features/file-manager/models/file-selector.model';
import { QueryProvider } from '@shared/queries/query-provider';
import { SetCurrentRightSideComponentAction } from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {FileManagementService} from "@core/services/file-management.service";

@Injectable({
  providedIn: 'root',
})

export class PlatformToolTrialBalanceMappingHandler implements IPlatformToolHandler {
  invalidateSubject = new InvalidateSubject();
  magicLinkModel: MagicLinkModel;
  magicLinkModelValueChanged = new EventEmitter<any>();
  activatedRoute?: ActivatedRoute;
  metaData?: any;
  selectedTabModel: TabModel;

  constructor(
    private router: Router,
    private commandService: CommandService,
    private platformQueryService: PlatformQueryService,
    private dialogService: DialogService,
    private snackBarService: SnackBarService,
    private platformSidebarStore: Store<PlatformSidebarState>,
    private fuseSplashScreenService: FuseSplashScreenService,
    private queryStore: Store<IQueryState>,
    private fileManagementService: FileManagementService
  ) {
  }
  executeCommand(command: PlatformCommand, callBack?: any) {
    this.executeCommandToApi(command);
  }
  valueChanged($event: GridValueChangeEvent) {
    console.log('[Event]', $event);
    if ($event.action) {
      this.handleActions($event);
    }
    else if ($event.fieldDefinition) {
      if (this.magicLinkModel.additionalCommands[$event.fieldDefinition.key]) {
        const command = this.magicLinkModel.additionalCommands[
          $event.fieldDefinition.key
        ];
        command.fieldValues['ItemId'] = $event.item.id;
        this.executeCommandToApi(command);
        return;
      }
      switch ($event.fieldDefinition.key) {
        default:
          this.openTrialBalanceMappingItem($event);
          break;
      }
    }
  }
  setSelectedTab(selectedTab: TabModel): void {
    this.selectedTabModel = selectedTab;
    if (selectedTab.tabData?.DetailsHelpId &&
      ((!this.magicLinkModel.fieldValues.hasOwnProperty('FlowId') || this.magicLinkModel.fieldValues.FlowId == null) && (!this.magicLinkModel.fieldValues.hasOwnProperty('NodeId') || this.magicLinkModel.fieldValues.NodeId == null))) {
      // ! [MAGIC-BAR-AUTO-OPEN]
      this.platformSidebarStore.dispatch(
        new SetCurrentRightSideComponentAction(
          { component: 'HelpNotesComponent', data: { isUserAction: false }, componentInfo: { id: 'HelpNotesComponent' } }));
    }
    this.invokeReferenceOptionsQuery();
  }
  destroy(): void {
  }

  private handleActions($event: GridValueChangeEvent) {
    // write the handle action code
    switch ($event.action.id) {
      case 'Open':
        this.openTrialBalanceMappingItem($event);
        break;
      case 'Duplicate':
        this.duplicateTrialBalanceMappingItem($event);
        break;
      case 'Download':
        this.downloadTrialBalanceMapping($event);
        break;
      default:
        this.defaultCommandExecution($event);
        break;
    }
  }

  private executeCommandToApi(command: PlatformCommand) {
    console.log('[Executing PlatformCommand] -> ', command.name);
    command = JSON.parse(JSON.stringify(command));
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();
      this.commandService
        .executePlatformCommand(command)
        .pipe(take(1))
        .subscribe(
          (res) => {
            console.log('Command Response', res);
            this.fuseSplashScreenService.hide();
            if (res.fieldValues['SnackBarMessage']) {
              const duration = res.fieldValues['SnackBarMessage'].length * 80;
              this.snackBarService.showInformation(
                res.fieldValues['SnackBarMessage'],
                duration
              );
            }
            if (res.fieldValues['RedirectUrl']) {
              const url = res.fieldValues['RedirectUrl'];
              const currentUrl = this.router.url;
              if (`/${url}` === currentUrl && res.fieldValues['Refresh']) {
                this.invalidateSubject.invalidate('Refresh');
              } else {
                this.router.navigateByUrl(url);
              }
              return;
            }
            if (res.fieldValues['RedirectCommand']) {
              this.executeCommand(res.fieldValues['RedirectCommand']);
              return;
            }
            if (res.fieldValues['Reload'] && res.fieldValues['Reload'] === true) {
              this.invalidateReferenceOptions();
            }
            if (res.fieldValues['DownloadFileResponseModel']) {
              const downloadResponseModel = res.fieldValues['DownloadFileResponseModel'];
              const fileId = downloadResponseModel?.fileId;
              this.fileManagementService.downloadClientFileWithId(fileId).subscribe(payload => {
                this.fuseSplashScreenService.hide();
                this.fileManagementService.openSaveFileWindow(payload, downloadResponseModel.fileName);
              });
            }
            this.invalidateSubject.invalidate();
          },
          (error) => {
            console.error('Failed ToolBarComponent command', error);
            this.fuseSplashScreenService.hide();
          }
        );
    }
  }

  private invalidateReferenceOptions() {
    const referenceOptionsQueryKey = this.magicLinkModel?.fieldValues['ReferenceOptionsQueryKey'];
    if (referenceOptionsQueryKey) {
      QueryProvider.invokeQuery(this.queryStore, referenceOptionsQueryKey, {});
    }
  }

  private openTrialBalanceMappingItem($event: GridValueChangeEvent) {
    const openCommand = this.magicLinkModel.additionalCommands['Open'];
    openCommand.fieldValues['TrialBalanceMappingItemId'] = $event.item.id;

    var workbookSetId = $event.item.cells['WorkbookSetId'].value;
    var directoryEntryId = $event.item.cells['DirectoryEntryId'].value;
    var tbQnrPath = $event.item.cells['TrialBalanceInputQnrDirectoryPath'].value;

    openCommand.fieldValues['WorkbookSetId'] = workbookSetId;
    openCommand.fieldValues['TrialBalanceExcelDirectoryId'] = directoryEntryId;
    openCommand.fieldValues['TrialBalanceInputQnrDirectoryPath'] = tbQnrPath;

    this.executeCommandToApi(openCommand);
  }

  private duplicateTrialBalanceMappingItem($event: GridValueChangeEvent) {
    const duplicateMappingCommand = this.magicLinkModel.additionalCommands['Duplicate'];
    duplicateMappingCommand.fieldValues['TrialBalanceMappingItemId'] = $event.item.id;
    duplicateMappingCommand.fieldValues['HasDialogInput'] = true;
    duplicateMappingCommand.fieldValues['Title'] = $event.item.cells['Title'].value;
    duplicateMappingCommand.fieldValues['DuplicateAction'] = true;

    this.executeCommandToApi(duplicateMappingCommand);
  }

  private downloadTrialBalanceMapping($event: GridValueChangeEvent) {
    const downloadMappingCommand = this.magicLinkModel.additionalCommands['Download'];
    downloadMappingCommand.fieldValues['TrialBalanceMappingItemId'] = $event.item.id;
    var workbookSetId = $event.item.cells['WorkbookSetId'].value;
    var directoryEntryId = $event.item.cells['DirectoryEntryId'].value;

    downloadMappingCommand.fieldValues['WorkbookSetId'] = workbookSetId;
    downloadMappingCommand.fieldValues['DirectoryEntryId'] = directoryEntryId;
    downloadMappingCommand.fieldValues['EventSourceId'] = workbookSetId;

    this.executeCommandToApi(downloadMappingCommand);
  }

  private defaultCommandExecution($event: GridValueChangeEvent) {
    const command = this.magicLinkModel.additionalCommands[$event.action.id];
    command.fieldValues['TrialBalanceMappingItemId'] = $event.item.id;

    this.executeCommandToApi(command);
  }

  private invokeReferenceOptionsQuery() {
    this.invalidateReferenceOptions();
  }

}